import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Facebook, Instagram, Save, Check, Trash2, ExternalLink, ShieldCheck, Loader2, Copy, Send, X, Sparkles } from "lucide-react";

export default function AdminIntegrations() {
  const [items, setItems] = useState([]);
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/integrations");
    setItems(data);
    const meta = data.find((d) => d.id === "meta");
    if (meta?.app_id) setAppId(meta.app_id);
  };

  useEffect(() => { load(); }, []);

  const saveMeta = async () => {
    if (!appId.trim()) {
      toast.error("Meta App ID is required");
      return;
    }
    setSaving(true);
    try {
      const body = { app_id: appId.trim() };
      if (appSecret.trim()) body.app_secret = appSecret.trim();
      await api.put("/admin/integrations/meta", body);
      toast.success("Meta credentials saved");
      setAppSecret("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearMeta = async () => {
    if (!window.confirm("Clear Meta credentials? Existing user OAuth connections will break.")) return;
    try {
      await api.delete("/admin/integrations/meta");
      toast.success("Meta credentials cleared");
      setAppId(""); setAppSecret("");
      await load();
    } catch (e) {
      toast.error("Clear failed");
    }
  };

  const meta = items.find((i) => i.id === "meta") || { configured: false };

  return (
    <div className="space-y-8" data-testid="admin-integrations-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2">Admin</div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Integrations</h1>
        <p className="text-white/50 mt-2 max-w-2xl text-sm">
          Configure third-party platform credentials here. These are stored in the database (not env) and used by all client OAuth flows.
        </p>
      </header>

      <section className="glass rounded-2xl p-6 lg:p-8 border border-indigo-500/30" data-testid="meta-integration-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 grid place-items-center">
            <Facebook className="h-6 w-6 text-white" />
            <Instagram className="h-5 w-5 text-white -ml-1" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-heading text-2xl font-black">Meta Graph API</h3>
              {meta.configured ? (
                <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/15 text-emerald-300 px-2 py-1 rounded-full" data-testid="meta-status">
                  <Check className="h-3 w-3" /> Configured
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs bg-amber-500/15 text-amber-300 px-2 py-1 rounded-full" data-testid="meta-status">
                  Not configured
                </span>
              )}
            </div>
            <p className="text-sm text-white/60 mt-2">
              Unlocks Facebook Pages + Instagram Business publishing for all client accounts. Create a Meta app at{" "}
              <a className="text-indigo-300 underline inline-flex items-center gap-1" href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">
                developers.facebook.com/apps <ExternalLink className="h-3 w-3" />
              </a>{" "}
              and add the OAuth redirect URI shown below.
            </p>

            <div className="grid md:grid-cols-2 gap-3 mt-4">
              <label className="block">
                <span className="text-xs text-white/40 uppercase tracking-[0.2em]">App ID</span>
                <input
                  type="text"
                  value={appId}
                  onChange={(e) => setAppId(e.target.value)}
                  placeholder="e.g. 123456789012345"
                  data-testid="meta-app-id-input"
                  className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
                />
              </label>
              <label className="block">
                <span className="text-xs text-white/40 uppercase tracking-[0.2em]">App Secret {meta.configured && <span className="normal-case text-white/30">(leave blank to keep)</span>}</span>
                <input
                  type="password"
                  value={appSecret}
                  onChange={(e) => setAppSecret(e.target.value)}
                  placeholder={meta.app_secret_masked || "••••••••••••••••"}
                  data-testid="meta-app-secret-input"
                  className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
                />
              </label>
            </div>

            <div className="mt-4 glass rounded-lg p-3 text-xs">
              <div className="text-white/40 uppercase tracking-[0.2em] mb-1">OAuth Redirect URI (whitelist in Meta App)</div>
              <code className="text-indigo-300 break-all" data-testid="meta-redirect-uri">
                {process.env.REACT_APP_BACKEND_URL}/api/meta/oauth/callback
              </code>
            </div>

            <div className="flex flex-wrap gap-3 mt-5">
              <button
                onClick={saveMeta}
                disabled={saving}
                data-testid="meta-save-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save credentials
              </button>
              {meta.configured && (
                <button
                  onClick={clearMeta}
                  data-testid="meta-clear-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg glass text-sm font-semibold text-rose-300 hover:bg-rose-500/10"
                >
                  <Trash2 className="h-4 w-4" /> Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="glass rounded-2xl p-6 lg:p-8" data-testid="meta-app-config-info">
        <h3 className="font-heading text-xl font-black mb-4 flex items-center gap-2">
          <ExternalLink className="h-5 w-5 text-indigo-400" /> Meta App configuration · paste these in developers.facebook.com
        </h3>
        <p className="text-sm text-white/60 mb-4">
          When you submit your Meta App for review, Facebook asks for these URLs. Copy each one into the matching field at
          {" "}<a className="text-indigo-300 underline" target="_blank" rel="noreferrer" href="https://developers.facebook.com/apps">developers.facebook.com/apps</a> → your app → Settings → Basic.
        </p>
        <div className="grid gap-3">
          <UrlRow label="Privacy Policy URL" url="https://makeyourbrand.in/legal/privacy" testid="meta-url-privacy" />
          <UrlRow label="Terms of Service URL" url="https://makeyourbrand.in/legal/terms" testid="meta-url-terms" />
          <UrlRow label="User Data Deletion URL" url="https://makeyourbrand.in/legal/data-deletion" testid="meta-url-deletion" />
          <UrlRow label="App Domain" url="makeyourbrand.in" testid="meta-url-domain" />
          <UrlRow label="Contact Email" url="sales@makeyourbrand.in" testid="meta-url-contact" />
          <UrlRow label="OAuth Redirect URI (Facebook Login → Settings)" url={`${process.env.REACT_APP_BACKEND_URL}/api/meta/oauth/callback`} testid="meta-url-oauth" />
          <UrlRow label="App Icon (1024×1024)" url="https://customer-assets.emergentagent.com/job_ai-agency-platform-3/artifacts/nrx9ad9i_MakeYourBrand.IN%20Logo.png" testid="meta-url-icon" />
          <UrlRow label="Default OG share image (1200×630)" url={`${process.env.REACT_APP_BACKEND_URL}/api/og/default`} testid="meta-url-og" />
        </div>
      </section>

      <SmtpPanel />
      <AIProviderPanel />
      <AdPlatformsPanel />
      <CloudflarePanel />

      <section className="glass rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <ShieldCheck className="h-6 w-6 text-indigo-400" />
          <div className="text-sm text-white/60">
            <strong className="text-white">Security:</strong> App secrets are masked on read; only the latest secret you save is used. Never share secrets in screenshots or logs. Use a Meta App with restricted IP access where possible.
          </div>
        </div>
      </section>
    </div>
  );
}

function SmtpPanel() {
  const [cfg, setCfg] = useState(null);
  const [toEmail, setToEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);

  const load = async () => {
    const [{ data: c }, { data: l }] = await Promise.all([
      api.get("/admin/email/config"),
      api.get("/admin/email/log?limit=10"),
    ]);
    setCfg(c); setLog(l);
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!toEmail) { toast.error("Recipient required"); return; }
    setSending(true);
    try {
      await api.post("/admin/email/test", { to: toEmail });
      toast.success("Test email sent");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  if (!cfg) return null;

  return (
    <section className="glass rounded-2xl p-6 lg:p-8" data-testid="smtp-panel">
      <h3 className="font-heading text-xl font-black mb-4 flex items-center gap-2">
        <Send className="h-5 w-5 text-indigo-400" /> SMTP · Internal email
        {cfg.configured ? (
          <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/15 text-emerald-300 px-2 py-1 rounded-full ml-2">
            <Check className="h-3 w-3" /> Configured
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs bg-amber-500/15 text-amber-300 px-2 py-1 rounded-full ml-2">Not configured</span>
        )}
      </h3>
      <div className="grid md:grid-cols-2 gap-3 mb-4 text-xs font-mono">
        <Stat k="Host" v={cfg.host} />
        <Stat k="Port" v={cfg.port} />
        <Stat k="Sender" v={`${cfg.sender_name} <${cfg.sender_email}>`} />
        <Stat k="Admin notify" v={cfg.admin_notify} />
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <input value={toEmail} onChange={(e) => setToEmail(e.target.value)}
          type="email" placeholder="Send test email to..."
          data-testid="smtp-test-to"
          className="flex-1 min-w-[220px] bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        <button onClick={send} disabled={sending} data-testid="smtp-test-send"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test
        </button>
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Recent activity</div>
        {log.length === 0 ? (
          <div className="text-xs text-white/40">No emails sent yet.</div>
        ) : (
          <div className="space-y-1">
            {log.map((l) => (
              <div key={l.id} className="glass rounded p-2 text-xs flex items-center gap-3" data-testid={`smtp-log-${l.id}`}>
                {l.ok ? <Check className="h-3 w-3 text-emerald-400 shrink-0" /> : <X className="h-3 w-3 text-rose-400 shrink-0" />}
                <span className="font-mono text-white/40 w-44 shrink-0">{new Date(l.sent_at).toLocaleString()}</span>
                <span className="text-white/70 flex-1 truncate">{l.subject}</span>
                <span className="text-white/40 truncate max-w-[200px]">{(l.to || []).join(", ")}</span>
                <span className="text-[10px] text-indigo-300 uppercase tracking-[0.15em]">{l.category}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ k, v }) {
  return (
    <div className="glass rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{k}</div>
      <div className="text-white/80 mt-1 break-all">{v || "—"}</div>
    </div>
  );
}

function AIProviderPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/ai/config");
    setCfg(data);
  };
  useEffect(() => { load(); }, []);

  const switchTo = async (id) => {
    setSaving(id);
    try {
      await api.put("/admin/ai/provider", { provider: id });
      toast.success(`AI text generation switched to ${id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Switch failed");
    } finally { setSaving(null); }
  };

  if (!cfg) return null;

  return (
    <section className="glass rounded-2xl p-6 lg:p-8" data-testid="ai-provider-panel">
      <h3 className="font-heading text-xl font-black mb-4 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-indigo-400" /> AI text provider
      </h3>
      <p className="text-sm text-white/60 mb-4">
        Choose which LLM powers all text generation across the platform (Power Hour posts, CRM next-step, SEO keywords, One-click captions). All providers use the Emergent LLM Key — no extra setup. Image generation stays on Gemini Nano Banana.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cfg.options.map((o) => {
          const active = o.id === cfg.active_provider;
          const label = { gemini: "Gemini 3 Flash", claude: "Claude Sonnet 4.5", openai: "ChatGPT (GPT-5)", "gpt-4o": "ChatGPT (GPT-4o)", haiku: "Claude Haiku 4.5" }[o.id] || o.id;
          return (
            <button key={o.id} onClick={() => !active && switchTo(o.id)} disabled={saving === o.id || active}
              data-testid={`ai-provider-${o.id}`}
              className={`text-left glass rounded-xl p-4 tilt-card border ${active ? "border-indigo-500/60 bg-indigo-500/[0.08]" : "border-white/5 hover:bg-white/[0.04]"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-heading font-black">{label}</span>
                {active && <span className="text-xs bg-emerald-500/15 text-emerald-300 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Check className="h-3 w-3" /> Active</span>}
              </div>
              <div className="text-xs text-white/40 font-mono">{o.vendor} · {o.model}</div>
              {saving === o.id && <Loader2 className="h-3 w-3 animate-spin mt-2 text-indigo-400" />}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function UrlRow({ label, url, testid }) {

  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast.success("Copied to clipboard");
  };
  return (
    <div className="flex items-center gap-3 glass rounded-lg p-3" data-testid={testid}>
      <div className="text-xs text-white/40 uppercase tracking-[0.2em] w-56 shrink-0">{label}</div>
      <code className="flex-1 text-xs text-indigo-300 font-mono break-all">{url}</code>
      <button onClick={copy} data-testid={`${testid}-copy`}
        className="px-2.5 py-1.5 rounded-lg glass text-xs hover:bg-white/5 inline-flex items-center gap-1">
        <Copy className="h-3 w-3" /> Copy
      </button>
    </div>
  );
}

function AdPlatformsPanel() {
  const [cfg, setCfg] = useState(null);
  const [g, setG] = useState({ developer_token: "", oauth_client_id: "", oauth_client_secret: "", login_customer_id: "" });
  const [li, setLi] = useState({ client_id: "", client_secret: "" });
  const [saving, setSaving] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/integrations/ads");
    setCfg(data);
    setG((prev) => ({ ...prev,
      oauth_client_id: data.google_ads?.oauth_client_id || "",
      login_customer_id: data.google_ads?.login_customer_id || "",
    }));
    setLi((prev) => ({ ...prev,
      client_id: data.linkedin_ads?.client_id || "",
    }));
  };
  useEffect(() => { load(); }, []);

  const saveGoogle = async () => {
    setSaving("google");
    try {
      const body = {};
      Object.entries(g).forEach(([k, v]) => { if (v && v.trim()) body[k] = v.trim(); });
      await api.put("/admin/integrations/google_ads", body);
      toast.success("Google Ads credentials saved");
      setG((p) => ({ ...p, developer_token: "", oauth_client_secret: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(null); }
  };

  const saveLinkedIn = async () => {
    setSaving("linkedin");
    try {
      const body = {};
      Object.entries(li).forEach(([k, v]) => { if (v && v.trim()) body[k] = v.trim(); });
      await api.put("/admin/integrations/linkedin_ads", body);
      toast.success("LinkedIn Ads credentials saved");
      setLi((p) => ({ ...p, client_secret: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(null); }
  };

  if (!cfg) return null;
  const gReady = cfg.google_ads?.configured;
  const liReady = cfg.linkedin_ads?.configured;

  return (
    <section className="glass rounded-2xl p-6 lg:p-8" data-testid="ad-platforms-panel">
      <h3 className="font-heading text-xl font-black mb-4 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-indigo-400" /> Ad platforms · Google &amp; LinkedIn
      </h3>
      <p className="text-sm text-white/60 mb-4">
        Configure platform-level developer credentials. Users still link their own per-platform Ad Account ID + token under /dashboard/ads. Meta Ads use the existing Meta OAuth flow (no extra config needed here).
      </p>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="glass rounded-xl p-5" data-testid="google-ads-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-black">Google Ads API</div>
            {gReady ? <span className="text-xs bg-emerald-500/15 text-emerald-300 px-2 py-1 rounded-full inline-flex items-center gap-1"><Check className="h-3 w-3" /> Configured</span>
                    : <span className="text-xs bg-amber-500/15 text-amber-300 px-2 py-1 rounded-full">Not configured</span>}
          </div>
          <div className="space-y-2 text-xs">
            <input value={g.developer_token} onChange={(e) => setG({ ...g, developer_token: e.target.value })}
              placeholder={cfg.google_ads?.developer_token_masked || "Developer Token"} type="password"
              data-testid="google-dev-token"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 font-mono" />
            <input value={g.oauth_client_id} onChange={(e) => setG({ ...g, oauth_client_id: e.target.value })}
              placeholder="OAuth Client ID"
              data-testid="google-client-id"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 font-mono" />
            <input value={g.oauth_client_secret} onChange={(e) => setG({ ...g, oauth_client_secret: e.target.value })}
              placeholder={cfg.google_ads?.oauth_client_secret_masked || "OAuth Client Secret"} type="password"
              data-testid="google-client-secret"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 font-mono" />
            <input value={g.login_customer_id} onChange={(e) => setG({ ...g, login_customer_id: e.target.value })}
              placeholder="Manager (MCC) Customer ID — optional"
              data-testid="google-mcc-id"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 font-mono" />
          </div>
          <button onClick={saveGoogle} disabled={saving === "google"} data-testid="google-save"
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-xs font-semibold hover:opacity-90 disabled:opacity-50">
            {saving === "google" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
          </button>
          <div className="text-[10px] text-white/40 mt-3">
            Get dev token: <a href="https://developers.google.com/google-ads/api/docs/get-started/dev-token" target="_blank" rel="noreferrer" className="text-indigo-300 underline">developers.google.com</a>
          </div>
        </div>

        <div className="glass rounded-xl p-5" data-testid="linkedin-ads-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-black">LinkedIn Marketing API</div>
            {liReady ? <span className="text-xs bg-emerald-500/15 text-emerald-300 px-2 py-1 rounded-full inline-flex items-center gap-1"><Check className="h-3 w-3" /> Configured</span>
                     : <span className="text-xs bg-amber-500/15 text-amber-300 px-2 py-1 rounded-full">Not configured</span>}
          </div>
          <div className="space-y-2 text-xs">
            <input value={li.client_id} onChange={(e) => setLi({ ...li, client_id: e.target.value })}
              placeholder="Client ID"
              data-testid="linkedin-client-id"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 font-mono" />
            <input value={li.client_secret} onChange={(e) => setLi({ ...li, client_secret: e.target.value })}
              placeholder={cfg.linkedin_ads?.client_secret_masked || "Client Secret"} type="password"
              data-testid="linkedin-client-secret"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 font-mono" />
          </div>
          <button onClick={saveLinkedIn} disabled={saving === "linkedin"} data-testid="linkedin-save"
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-xs font-semibold hover:opacity-90 disabled:opacity-50">
            {saving === "linkedin" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
          </button>
          <div className="text-[10px] text-white/40 mt-3">
            Apply at: <a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noreferrer" className="text-indigo-300 underline">linkedin.com/developers</a>
          </div>
        </div>
      </div>
    </section>
  );
}

function CloudflarePanel() {
  const [cfg, setCfg] = useState(null);
  const [token, setToken] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/integrations/cloudflare");
    setCfg(data);
    setZoneId(data.zone_id || "");
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (token.trim()) body.api_token = token.trim();
      if (zoneId.trim()) body.zone_id = zoneId.trim();
      await api.put("/admin/integrations/cloudflare", body);
      toast.success("Cloudflare for SaaS credentials saved");
      setToken("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!cfg) return null;
  return (
    <section className="glass rounded-2xl p-6 lg:p-8" data-testid="cloudflare-panel">
      <h3 className="font-heading text-xl font-black mb-4 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-orange-400" /> Cloudflare for SaaS · Auto-SSL
        {cfg.configured && (
          <span className="text-xs bg-emerald-500/15 text-emerald-300 px-2 py-1 rounded-full inline-flex items-center gap-1">
            <Check className="h-3 w-3" /> Configured
          </span>
        )}
      </h3>
      <p className="text-sm text-white/60 mb-4">
        When configured, every white-label custom domain that passes DNS verification will be automatically registered with Cloudflare's <a href="https://developers.cloudflare.com/cloudflare-for-platforms/cloudflare-for-saas/" target="_blank" rel="noreferrer" className="text-indigo-300 underline">Cloudflare for SaaS</a> Custom Hostnames API. Cloudflare issues a free DV TLS certificate and serves the tenant via your zone — no per-domain Caddy or Let's Encrypt management needed.
      </p>
      <div className="grid lg:grid-cols-2 gap-3 text-xs">
        <input value={token} onChange={(e) => setToken(e.target.value)}
          placeholder={cfg.api_token_masked || "API Token (Zone → SSL and Certificates → Edit)"}
          type="password"
          data-testid="cloudflare-token"
          className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 font-mono" />
        <input value={zoneId} onChange={(e) => setZoneId(e.target.value)}
          placeholder="Zone ID (32-hex)"
          data-testid="cloudflare-zone-id"
          className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 font-mono" />
      </div>
      <button onClick={save} disabled={saving} data-testid="cloudflare-save"
        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-xs font-semibold hover:opacity-90 disabled:opacity-50">
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save Cloudflare credentials
      </button>
      <div className="text-[10px] text-white/40 mt-3">
        Create token at <a href="https://dash.cloudflare.com/profile/api-tokens" target="_blank" rel="noreferrer" className="text-indigo-300 underline">dash.cloudflare.com/profile/api-tokens</a> with scope: <code>Zone → SSL and Certificates → Edit</code> on the SaaS zone. Zone ID is on the zone's overview page.
      </div>
    </section>
  );
}

